import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, DateInput, DocumentTextIcon, SearchIcon } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { FieldLabel, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery } from './shared/useAccountingReportFilters';

type Row = {
  companyId: number;
  companyName: string;
  accountId: number;
  accountCode: string;
  accountName: string;
  linkNumber: string;
  balance: number;
  earliestDueDate: string | null;
  daysPastDue: number;
  agingBucket: string;
};

export const Aging: React.FC = () => {
  const { colors } = useTheme();
  const [asOfDate, setAsOfDate] = useState('');
  const [run, setRun] = useState(false);

  const url =
    run
      ? buildReportQuery('/accounting/reports/aging', { asOfDate: asOfDate || undefined })
      : null;
  const { data: rows, isLoading } = useSWR<Row[]>(url, fetcher);
  const data = rows ?? [];

  const columns = useMemo<Column<Row>[]>(
    () => [
      { key: 'companyName', header: 'Company' },
      { key: 'accountCode', header: 'Account', render: (_v, r) => r.accountName },
      { key: 'linkNumber', header: 'Link #' },
      { key: 'balance', header: 'Balance', align: 'right', render: formatReportAmount },
      { key: 'earliestDueDate', header: 'Due', render: (v) => v || '—' },
      { key: 'daysPastDue', header: 'Days', align: 'right' },
      { key: 'agingBucket', header: 'Bucket' },
      {
        key: 'actions',
        header: '',
        render: (_v, row) => (
          <Link
            to={`/accounting/reports/link-statement?companyId=${row.companyId}&accountId=${row.accountId}&linkNumber=${encodeURIComponent(row.linkNumber)}&dateTo=${asOfDate || new Date().toISOString().slice(0, 10)}&dateFrom=2000-01-01`}
            title="Link statement"
          >
            <DocumentTextIcon className="h-4 w-4" />
          </Link>
        ),
      },
    ],
    [asOfDate],
  );

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <div>
          <FieldLabel>As of date</FieldLabel>
          <DateInput value={asOfDate} onChange={(e) => { setAsOfDate(e.target.value); setRun(false); }} showFormatHint={false} />
        </div>
        <Link to="/accounting/reports/open-items" className="text-sm text-indigo-600 hover:underline pb-2">
          Open items detail
        </Link>
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Button variant="default" onClick={() => setRun(true)} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  const effectiveAsOf = asOfDate || new Date().toISOString().slice(0, 10);

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={run}
      loading={isLoading}
      rows={data}
      columns={columns}
      entityName="Aging"
      exportFileBase="aging"
      exportDateTo={effectiveAsOf}
      statusBadge={
        <Badge variant="info" className="font-normal">
          As of {formatDateGlobal(effectiveAsOf)}
        </Badge>
      }
      recordCount={data.length}
      readyMessage="Set as-of date and click Search."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={data}
        columns={columns}
        keyExtractor={(r) => `${r.companyId}-${r.accountCode}-${r.linkNumber}`}
        emptyMessage="No aging data."
        stickyHeader
      />
    </AccountingReportShell>
  );
};
